/**
 * The browser runtime (06 §14.4): `t`, `tp`, `tHtml`, `cardName`, and locale switching without a reload.
 * A leaf: it imports only this package's own files, so the web entry stays small.
 */
import type { HtmlKey, MsgKey, MsgParams, PluralKey, PluralParams } from './keys.gen';
import { INTL_LOCALES, type Locale } from './locales';

/** A compiled message table (one locale), as the Vite plugin emits it. */
export type MessageTable = Readonly<Record<string, string>>;

/** Loads one locale's table; the Vite plugin's `virtual:tarock-i18n` exports these as `loaders`. */
export type Loaders = { readonly en: () => Promise<MessageTable> } & Partial<
  Readonly<Record<Locale, () => Promise<MessageTable>>>
>;

/** Placeholder values; numbers are formatted with `Intl.NumberFormat` for the active locale. */
export type Params = Readonly<Record<string, string | number>>;

/** Required only when the key has placeholders. */
type Args<P> = keyof P extends never ? [] : [params: P];

interface State {
  locale: Locale;
  table: MessageTable;
  fallback: MessageTable;
  loaders: Loaders | null;
  /** Namespaces loaded later with their chunk (the admin strings, ADR 0015); merged into every locale switch. */
  extras: Loaders[];
  onMissing: ((key: string, locale: Locale) => void) | null;
  /** Bumped per switch, so that only the latest `setLocale` wins. */
  token: number;
}

const state: State = { locale: 'en', table: {}, fallback: {}, loaders: null, extras: [], onMissing: null, token: 0 };
const listeners = new Set<(locale: Locale) => void>();
const plurals = new Map<string, Intl.PluralRules>();
const numbers = new Map<string, Intl.NumberFormat>();

/** The active locale's Intl tag (`en` → `en-GB`). */
export function intlLocale(locale: Locale = state.locale): string {
  return INTL_LOCALES[locale];
}

function pluralRules(locale: Locale): Intl.PluralRules {
  const tag = intlLocale(locale);
  let r = plurals.get(tag);
  if (r === undefined) {
    r = new Intl.PluralRules(tag);
    plurals.set(tag, r);
  }
  return r;
}

function formatParam(v: string | number): string {
  if (typeof v === 'string') {
    return v;
  }
  const tag = intlLocale();
  let f = numbers.get(tag);
  if (f === undefined) {
    f = new Intl.NumberFormat(tag);
    numbers.set(tag, f);
  }
  return f.format(v);
}

/** Fills `{name}` placeholders; unknown names stay visible, so a missing param is easy to spot. */
function fill(template: string, params: Params | undefined, esc: (s: string) => string): string {
  if (params === undefined) {
    return template;
  }
  return template.replace(/\{([A-Za-z_]\w*)\}/g, (token, name: string) => {
    const v = params[name];
    return v === undefined ? token : esc(formatParam(v));
  });
}

const identity = (s: string): string => s;
const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** Active table, then en; else reports the miss and returns the key itself. */
function lookup(key: string): string {
  const v = state.table[key] ?? state.fallback[key];
  if (v !== undefined) {
    return v;
  }
  state.onMissing?.(key, state.locale);
  return key;
}

/** A plain-text message. Params are required exactly when the key has placeholders. */
export function t<K extends MsgKey>(key: K, ...args: Args<MsgParams[K]>): string {
  return fill(lookup(key), args[0] as Params | undefined, identity);
}

/**
 * A plural message: the category comes from `Intl.PluralRules(locale).select(n)`, falling back to `.other`. `n`
 * fills `{n}`.
 */
export function tp<K extends PluralKey>(key: K, n: number, ...args: Args<PluralParams[K]>): string {
  // The active table's own forms first; the en fallback is indexed by en's categories.
  const own = state.table[`${key}.${pluralRules(state.locale).select(n)}`] ?? state.table[`${key}.other`];
  const en = state.fallback[`${key}.${pluralRules('en').select(n)}`];
  const template = own ?? en ?? lookup(`${key}.other`);
  return fill(template, { ...(args[0] as Params | undefined), n }, identity);
}

/**
 * A message with inline markup, for `innerHTML` / `dangerouslySetInnerHTML`. The markup was checked against the
 * allow-list (`strong em a code br`) at build time; params are HTML-escaped here.
 */
export function tHtml<K extends HtmlKey>(key: K, ...args: Args<MsgParams[K]>): string {
  return fill(lookup(key), args[0] as Params | undefined, escapeHtml);
}

/** The localized name of a card code (`XXI` → "XXI Mond", `HK` → "King of Hearts"; 07 §5.6). */
export function cardName(code: string): string {
  return lookup(`card.${code}`);
}

/** The active locale. */
export function getLocale(): Locale {
  return state.locale;
}

/** Subscribes to locale switches (e.g. to update a signal); returns the unsubscribe function. */
export function onLocaleChange(listener: (locale: Locale) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Sets `<html lang>` when running in a document. */
function setDocumentLang(locale: Locale): void {
  const doc = (globalThis as { document?: { documentElement?: { lang: string } } }).document;
  if (doc?.documentElement !== undefined) {
    doc.documentElement.lang = intlLocale(locale);
  }
}

/** Installs tables directly (tests, or a build that inlines en). `fallback` defaults to `table` for en. */
export function setMessages(locale: Locale, table: MessageTable, fallback?: MessageTable): void {
  state.token++;
  state.locale = locale;
  state.table = table;
  state.fallback = fallback ?? (locale === 'en' ? table : state.fallback);
  setDocumentLang(locale);
  for (const l of listeners) {
    l(locale);
  }
}

/** `locale`'s tables of every registered extra namespace, merged (a namespace without that locale adds nothing). */
async function extrasFor(locale: Locale, extras: readonly Loaders[] = state.extras): Promise<MessageTable> {
  const tables = await Promise.all(extras.map((l) => l[locale]?.() ?? Promise.resolve({})));
  return Object.assign({}, ...tables) as MessageTable;
}

/** Loads `locale` (falling back to en when it has no loader) and switches to it without a reload. */
export async function setLocale(locale: Locale): Promise<Locale> {
  const loaders = state.loaders;
  if (loaders === null) {
    throw new Error('i18n: call initI18n first');
  }
  const token = ++state.token;
  const load = loaders[locale];
  const target = load === undefined ? 'en' : locale;
  const cached = Object.keys(state.fallback).length > 0 ? state.fallback : null;
  const [fallback, table, extra] = await Promise.all([
    cached ?? Promise.all([loaders.en(), extrasFor('en')]).then(([base, more]) => ({ ...base, ...more })),
    target === 'en' ? null : (load as () => Promise<MessageTable>)(),
    target === 'en' ? {} : extrasFor(target),
  ]);
  if (token === state.token) {
    setMessages(target, table ? { ...table, ...extra } : fallback, fallback);
  }
  return target;
}

/**
 * Adds a namespace that ships with a lazy chunk (the admin strings, ADR 0015): loads its en table and the active
 * locale's, merges them in, and keeps them for later locale switches. Await before rendering the chunk.
 */
export async function extendI18n(extra: Loaders): Promise<void> {
  if (state.extras.includes(extra)) {
    return;
  }
  state.extras.push(extra);
  const locale = state.locale;
  const [en, own] = await Promise.all([extra.en(), locale === 'en' ? null : extrasFor(locale, [extra])]);
  const onEn = state.table === state.fallback;
  state.fallback = { ...state.fallback, ...en };
  // a switch that started meanwhile loads this namespace itself (it is registered)
  if (state.locale === locale) {
    state.table = onEn ? state.fallback : { ...state.table, ...own };
  }
  for (const l of listeners) {
    l(state.locale);
  }
}

/** Options of `initI18n`. */
export interface InitOptions {
  /** From `virtual:tarock-i18n`. */
  readonly loaders: Loaders;
  /** Usually `pickLocale(…)`; defaults to `en`. */
  readonly locale?: Locale;
  /** Called for a key that neither the locale nor en has (the key itself is shown). */
  readonly onMissing?: (key: string, locale: Locale) => void;
}

/** Loads en (the fallback) and the chosen locale; resolves with the locale in effect. Await before rendering. */
export function initI18n(opts: InitOptions): Promise<Locale> {
  state.loaders = opts.loaders;
  state.onMissing = opts.onMissing ?? null;
  state.fallback = {};
  return setLocale(opts.locale ?? 'en');
}
